// ENG-442 — Unreal-parity additive baking.
//
// Unreal exposes two additive controls on an animation asset:
//   • Additive Anim Type  (none | local | mesh)        → how the delta is computed
//   • Base Pose Type       (none | ref_pose | anim_scaled | anim_frame | local_frame)
//                                                       → what pose counts as "zero"
//   • Ref Frame Index      (int)                        → which frame of the base
//
// Those settings are stripped during FBX→GLB export, so they're re-declared as
// server-side fields on animation_assets and reproduced here at GLB load time.
// This mirrors Unreal, where the additive delta is baked at asset-build time
// (not at the ApplyAdditive graph node), so nothing here runs per frame.
//
// `local`/`local_frame`, `local`/`anim_frame`, and `local`/`ref_pose` reduce to
// native THREE.AnimationUtils.makeClipAdditive calls. `mesh` and `anim_scaled`
// have no THREE equivalent and are baked manually below.

import * as THREE from 'three'

// makeClipAdditive treats `referenceFrame` as a frame index converted to time
// via `fps`. Our conversion pipeline samples clips at 30fps, so a Ref Frame
// Index of N maps to N/30 seconds into the reference clip.
const DEFAULT_FPS = 30

// Read the rig's rest (bind) pose straight off the loaded rig scene. At load
// time — before the mixer plays anything — every bone sits at its rest local
// transform, which is exactly Unreal's "Skeleton Reference Pose".
// Returns Map<boneName, { position:[3], quaternion:[4], scale:[3] }>.
export function buildRestPose(rigScene) {
  const rest = new Map()
  rigScene.traverse((n) => {
    if (!n.isBone && !n.isObject3D) return
    if (!n.name) return
    rest.set(n.name, {
      position: [n.position.x, n.position.y, n.position.z],
      quaternion: [n.quaternion.x, n.quaternion.y, n.quaternion.z, n.quaternion.w],
      scale: [n.scale.x, n.scale.y, n.scale.z],
    })
  })
  return rest
}

// Parse "boneName.property" (property = position | quaternion | scale).
function splitTrack(trackName) {
  const dot = trackName.lastIndexOf('.')
  if (dot < 0) return { node: trackName, prop: '' }
  return { node: trackName.substring(0, dot), prop: trackName.substring(dot + 1) }
}

// Build a synthetic 1-frame reference clip from the rest pose, with one track
// per track in `targetClip` so makeClipAdditive can match by name. Tracks whose
// bone isn't in the rest pose are skipped (makeClipAdditive then leaves the
// corresponding target track unchanged).
function referenceClipFromRestPose(targetClip, restPose) {
  const tracks = []
  for (const t of targetClip.tracks) {
    const { node, prop } = splitTrack(t.name)
    const leaf = node.split('/').pop()
    const rest = restPose.get(leaf) ?? restPose.get(node)
    if (!rest) continue
    if (prop === 'quaternion') {
      tracks.push(new THREE.QuaternionKeyframeTrack(t.name, [0], rest.quaternion.slice()))
    } else if (prop === 'position') {
      tracks.push(new THREE.VectorKeyframeTrack(t.name, [0], rest.position.slice()))
    } else if (prop === 'scale') {
      tracks.push(new THREE.VectorKeyframeTrack(t.name, [0], rest.scale.slice()))
    }
  }
  return new THREE.AnimationClip(targetClip.name + '__refpose', -1, tracks)
}

// Resolve a possibly-missing/partial settings object into a concrete spec.
// Clips that are additive but carry no explicit config fall back to the
// historical behavior: Local Space, referenced against frame 0 of themselves
// (i.e. a bare makeClipAdditive(clip) call).
export function resolveAdditiveSettings(settings) {
  const s = settings ?? {}
  const additiveType = s.additiveType && s.additiveType !== 'none' ? s.additiveType : 'local'
  let basePoseType = s.basePoseType && s.basePoseType !== 'none' ? s.basePoseType : 'local_frame'
  return {
    additiveType,
    basePoseType,
    basePoseClipName: s.basePoseClipName ?? null,
    refFrameIndex: Number.isFinite(s.refFrameIndex) ? s.refFrameIndex : 0,
  }
}

// Bake `clip` into an additive delta IN PLACE, per the resolved settings.
// ctx = { clipsByName: Map<name, THREE.AnimationClip>, restPose: Map, fps?: number }
export function bakeAdditive(clip, settings, ctx) {
  const { additiveType, basePoseType, basePoseClipName, refFrameIndex } = resolveAdditiveSettings(settings)
  const fps = ctx.fps ?? DEFAULT_FPS

  if (additiveType === 'mesh') {
    bakeMeshSpaceAdditive(clip, { basePoseType, basePoseClipName, refFrameIndex }, ctx, fps)
    return
  }

  // Local-space additive — resolve which pose is subtracted.
  switch (basePoseType) {
    case 'local_frame':
      THREE.AnimationUtils.makeClipAdditive(clip, refFrameIndex, clip, fps)
      return
    case 'anim_frame': {
      const base = basePoseClipName ? ctx.clipsByName.get(basePoseClipName) : null
      if (!base) {
        throw new Error(`[additiveBake] clip "${clip.name}" uses base_pose_type=anim_frame but base pose clip "${basePoseClipName}" was not found among loaded clips`)
      }
      THREE.AnimationUtils.makeClipAdditive(clip, refFrameIndex, base, fps)
      return
    }
    case 'ref_pose': {
      if (!ctx.restPose) {
        throw new Error(`[additiveBake] clip "${clip.name}" uses base_pose_type=ref_pose but no rest pose was provided`)
      }
      const refClip = referenceClipFromRestPose(clip, ctx.restPose)
      THREE.AnimationUtils.makeClipAdditive(clip, 0, refClip, fps)
      return
    }
    case 'anim_scaled': {
      const base = basePoseClipName ? ctx.clipsByName.get(basePoseClipName) : null
      if (!base) {
        throw new Error(`[additiveBake] clip "${clip.name}" uses base_pose_type=anim_scaled but base pose clip "${basePoseClipName}" was not found among loaded clips`)
      }
      bakeAnimScaledAdditive(clip, base)
      return
    }
    default:
      THREE.AnimationUtils.makeClipAdditive(clip, refFrameIndex, clip, fps)
  }
}

// ── Phase 3: custom bake modes (no THREE equivalent) ──────────────────────────

// Sample a track's value at an arbitrary time using its interpolant. Returns a
// flat array sized to the track's valueSize.
function sampleTrack(track, time) {
  const interpolant = track.createInterpolant()
  const out = interpolant.evaluate(time)
  return Array.from(out)
}

// anim_scaled (Unreal ABPT_AnimScaled): the base pose is the WHOLE base clip,
// time-normalized to the target clip's duration, subtracted per frame.
// makeClipAdditive only subtracts a single reference frame, so we do the
// per-keyframe subtraction ourselves.
function bakeAnimScaledAdditive(clip, baseClip) {
  const targetLen = clip.duration || 1
  const baseLen = baseClip.duration || 1
  const baseByName = new Map(baseClip.tracks.map(t => [t.name, t]))

  for (const track of clip.tracks) {
    const baseTrack = baseByName.get(track.name)
    if (!baseTrack) continue
    const { prop } = splitTrack(track.name)
    const times = track.times
    const values = track.values
    const stride = track.getValueSize()

    for (let i = 0; i < times.length; i++) {
      const t = times[i]
      const baseTime = (t / targetLen) * baseLen
      const baseVal = sampleTrack(baseTrack, baseTime)
      const off = i * stride
      if (prop === 'quaternion') {
        // additive = base^-1 * target  (delta rotation)
        const tq = new THREE.Quaternion(values[off], values[off + 1], values[off + 2], values[off + 3])
        const bq = new THREE.Quaternion(baseVal[0], baseVal[1], baseVal[2], baseVal[3]).invert()
        const dq = bq.multiply(tq)
        values[off] = dq.x; values[off + 1] = dq.y; values[off + 2] = dq.z; values[off + 3] = dq.w
      } else {
        // position/scale: component subtraction (matches makeClipAdditive)
        for (let c = 0; c < stride; c++) values[off + c] = values[off + c] - baseVal[c]
      }
    }
  }
}

// mesh (Unreal AAT_RotationOffsetMeshSpace): rotation deltas are computed in
// MESH (component) space rather than each bone's local parent space, then
// converted back to a local-space delta clip the mixer can play. Translation
// stays local. Mirrors Unreal's GetBonePose_AdditiveMeshRotationOnly.
//
// For each rotation track at time t:
//   meshTarget = parentMeshRot * localTarget          (walk up the hierarchy)
//   meshBase   = parentMeshRot * localBase
//   meshDelta  = meshBase^-1 * meshTarget             (delta in mesh space)
//   localDelta = meshDelta                            (stored as a local-space
//                                                       additive the mixer adds)
// We need bone parent rotations, which come from the rig skeleton hierarchy.
function bakeMeshSpaceAdditive(clip, opts, ctx, fps) {
  if (!ctx.boneParents || !ctx.restPose) {
    throw new Error(`[additiveBake] clip "${clip.name}" uses additive_type=mesh but the skeleton hierarchy was not provided`)
  }
  // Resolve the base (reference) pose provider for each bone at a given time.
  // For mesh space we support the same base-pose choices; default local_frame/0.
  const baseProvider = makeBaseRotationProvider(clip, opts, ctx, fps)

  // Precompute target rotation tracks by bone for parent lookups.
  const rotTrackByBone = new Map()
  for (const track of clip.tracks) {
    const { node, prop } = splitTrack(track.name)
    if (prop === 'quaternion') rotTrackByBone.set(node.split('/').pop(), track)
  }

  // Mesh-space rotation of a bone at time t = product of local rotations from
  // root down to the bone. We compute it on demand, memoized per (bone,frame).
  const meshRotAt = (boneLeaf, time, source) => {
    const chain = []
    let cur = boneLeaf
    while (cur) {
      chain.push(cur)
      cur = ctx.boneParents.get(cur)
    }
    chain.reverse()
    const q = new THREE.Quaternion()
    for (const b of chain) {
      const local = source(b, time)
      q.multiply(local)
    }
    return q
  }

  const localTargetRot = (boneLeaf, time) => {
    const tr = rotTrackByBone.get(boneLeaf)
    if (tr) {
      const v = sampleTrack(tr, time)
      return new THREE.Quaternion(v[0], v[1], v[2], v[3])
    }
    const rest = ctx.restPose.get(boneLeaf)
    return rest ? new THREE.Quaternion(...rest.quaternion) : new THREE.Quaternion()
  }

  for (const track of clip.tracks) {
    const { node, prop } = splitTrack(track.name)
    if (prop !== 'quaternion') continue // translation/scale stay local untouched here
    const leaf = node.split('/').pop()
    const times = track.times
    const values = track.values
    for (let i = 0; i < times.length; i++) {
      const t = times[i]
      const meshTarget = meshRotAt(leaf, t, localTargetRot)
      const meshBase = meshRotAt(leaf, t, (b, time) => baseProvider(b, time))
      const meshDelta = meshBase.clone().invert().multiply(meshTarget)
      const off = i * 4
      values[off] = meshDelta.x; values[off + 1] = meshDelta.y
      values[off + 2] = meshDelta.z; values[off + 3] = meshDelta.w
    }
  }

  // Translation/scale: subtract the base frame like local additive so the
  // gesture doesn't double up root translation/scale.
  bakeLocalTranslationScaleOnly(clip, opts, ctx, fps)
}

// Provides the LOCAL base rotation for a bone at a time, per the chosen base
// pose type (used by mesh-space accumulation).
function makeBaseRotationProvider(clip, opts, ctx, fps) {
  const { basePoseType, basePoseClipName, refFrameIndex } = opts
  const restRot = (boneLeaf) => {
    const rest = ctx.restPose.get(boneLeaf)
    return rest ? new THREE.Quaternion(...rest.quaternion) : new THREE.Quaternion()
  }
  if (basePoseType === 'ref_pose') {
    return (boneLeaf) => restRot(boneLeaf)
  }
  // anim_frame / local_frame: a single frame of a clip (base clip or self).
  const baseClip = basePoseType === 'anim_frame' && basePoseClipName
    ? ctx.clipsByName.get(basePoseClipName)
    : clip
  if (!baseClip) {
    throw new Error(`[additiveBake] mesh-space base pose clip "${basePoseClipName}" not found`)
  }
  const baseRotByBone = new Map()
  for (const tr of baseClip.tracks) {
    const { node, prop } = splitTrack(tr.name)
    if (prop === 'quaternion') baseRotByBone.set(node.split('/').pop(), tr)
  }
  const refTime = (refFrameIndex ?? 0) / fps
  return (boneLeaf) => {
    const tr = baseRotByBone.get(boneLeaf)
    if (tr) {
      const v = sampleTrack(tr, refTime)
      return new THREE.Quaternion(v[0], v[1], v[2], v[3])
    }
    return restRot(boneLeaf)
  }
}

// Subtract the base frame from translation/scale tracks only (local space).
function bakeLocalTranslationScaleOnly(clip, opts, ctx, fps) {
  const { basePoseType, basePoseClipName, refFrameIndex } = opts
  const baseClip = basePoseType === 'anim_frame' && basePoseClipName
    ? ctx.clipsByName.get(basePoseClipName)
    : clip
  const baseByName = new Map((baseClip?.tracks ?? []).map(t => [t.name, t]))
  const refTime = (refFrameIndex ?? 0) / fps
  for (const track of clip.tracks) {
    const { prop } = splitTrack(track.name)
    if (prop === 'quaternion') continue
    const baseTrack = baseByName.get(track.name)
    if (!baseTrack) continue
    const baseVal = sampleTrack(baseTrack, refTime)
    const stride = track.getValueSize()
    const values = track.values
    for (let i = 0; i < track.times.length; i++) {
      const off = i * stride
      for (let c = 0; c < stride; c++) values[off + c] = values[off + c] - baseVal[c]
    }
  }
}

// Build a child→parent bone-name map from the rig scene hierarchy (leaf names).
export function buildBoneParents(rigScene) {
  const parents = new Map()
  rigScene.traverse((n) => {
    if (!n.name) return
    if (n.parent && n.parent.name) parents.set(n.name, n.parent.name)
  })
  return parents
}

// Test hook: e2e specs build small clips and assert the bake math without
// depending on a backend that has the ENG-442 columns. THREE is bundled here
// so tests don't need their own copy.
if (typeof window !== 'undefined') {
  window.__ADDITIVE_BAKE__ = {
    THREE,
    bakeAdditive,
    resolveAdditiveSettings,
    buildRestPose,
    buildBoneParents,
  }
}
